import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { Hourglass, CheckCircle, XCircle, Package, ClipboardList, Layers, Plus, Pencil, Trash2, X, EyeOff, Eye, Image as ImageIcon, Wallet, Maximize2 } from 'lucide-react';

export default function AdminPackages() {
  const [mainTab, setMainTab] = useState('requests'); // 'requests' | 'plans'

  // ===================== Purchase Requests (existing) =====================
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);
  const [viewingScreenshot, setViewingScreenshot] = useState(null);

  const fetchPackages = useCallback(() => {
    setLoading(true);
    api.get('/packages/all').then(r => setPackages(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this package?')) return;
    setActionLoading(id + '_approve');
    try {
      const res = await api.put(`/packages/${id}/approve`);
      toast.success(res.data.message);
      fetchPackages();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve');
    } finally { setActionLoading(null); }
  };

  const handleReject = async (id) => {
    const note = prompt('Reason for rejection (optional):') || '';
    setActionLoading(id + '_reject');
    try {
      const res = await api.put(`/packages/${id}/reject`, { adminNote: note });
      toast.success(res.data.message);
      fetchPackages();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject');
    } finally { setActionLoading(null); }
  };

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;

  const filtered = packages.filter(p => filter === 'all' || p.status === filter);
  const counts = { pending: packages.filter(p => p.status === 'pending').length, active: packages.filter(p => p.status === 'active').length, rejected: packages.filter(p => p.status === 'rejected').length };

  // ===================== Manage Plans (new) =====================
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({ name: '', amount: '', validityDays: '365', description: '', features: '', isActive: true });
  const [planSubmitting, setPlanSubmitting] = useState(false);

  const fetchPlans = useCallback(() => {
    setPlansLoading(true);
    api.get('/plans/admin/all').then(r => setPlans(r.data)).catch(console.error).finally(() => setPlansLoading(false));
  }, []);

  useEffect(() => { if (mainTab === 'plans') fetchPlans(); }, [mainTab, fetchPlans]);

  const openAddPlan = () => {
    setEditingPlan(null);
    setPlanForm({ name: '', amount: '', validityDays: '365', description: '', features: '', isActive: true });
    setPlanModalOpen(true);
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name,
      amount: plan.amount,
      validityDays: plan.validityDays,
      description: plan.description || '',
      features: (plan.features || []).join('\n'),
      isActive: plan.isActive
    });
    setPlanModalOpen(true);
  };

  const closePlanModal = () => { setPlanModalOpen(false); setEditingPlan(null); };

  const handlePlanFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPlanForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePlanSubmit = async (e) => {
    e.preventDefault();
    if (!planForm.name.trim()) return toast.error('Plan name is required');
    if (!planForm.amount || Number(planForm.amount) <= 0) return toast.error('Amount must be greater than 0');

    const payload = {
      name: planForm.name.trim(),
      amount: Number(planForm.amount),
      validityDays: Number(planForm.validityDays) || 365,
      description: planForm.description.trim(),
      features: planForm.features.split('\n').map(f => f.trim()).filter(Boolean),
      isActive: planForm.isActive
    };

    setPlanSubmitting(true);
    try {
      if (editingPlan) {
        const res = await api.put(`/plans/${editingPlan._id}`, payload);
        toast.success(res.data.message);
      } else {
        const res = await api.post('/plans', payload);
        toast.success(res.data.message);
      }
      closePlanModal();
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save plan');
    } finally {
      setPlanSubmitting(false);
    }
  };

  const handleDeletePlan = async (plan) => {
    if (!window.confirm(`Delete the "${plan.name}" plan? This won't affect users who already purchased it — it just removes it from the purchase list.`)) return;
    try {
      const res = await api.delete(`/plans/${plan._id}`);
      toast.success(res.data.message);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete plan');
    }
  };

  return (
    <DashboardLayout>
      <h1 className="page-title">Package Management</h1>
      <p className="page-subtitle">Approve purchase requests, and create the packages users can buy</p>

      {/* Top-level section switch */}
      <div className="tab-bar" style={{ marginBottom: 20 }}>
        <button className={`tab-btn ${mainTab === 'requests' ? 'active' : ''}`} onClick={() => setMainTab('requests')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ClipboardList size={14} /> Purchase Requests
        </button>
        <button className={`tab-btn ${mainTab === 'plans' ? 'active' : ''}`} onClick={() => setMainTab('plans')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Layers size={14} /> Manage Plans
        </button>
      </div>

      {mainTab === 'requests' ? (
        <>
          {/* Summary */}
          <div className="stats-grid" style={{ marginBottom: 20 }}>
            {[
              { label: 'Pending', value: counts.pending, color: '#f59e0b', icon: Hourglass },
              { label: 'Active', value: counts.active, color: '#22c55e', icon: CheckCircle },
              { label: 'Rejected', value: counts.rejected, color: '#ef4444', icon: XCircle },
              { label: 'Total', value: packages.length, color: '#3b82f6', icon: Package },
            ].map(s => (
              <div key={s.label} className="stat-card">
                <div className="stat-icon" style={{ background: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color }}><s.icon size={22} /></div>
                <div className="stat-info">
                  <div className="stat-label">{s.label}</div>
                  <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="tab-bar" style={{ marginBottom: 20 }}>
            {[['pending', Hourglass, 'Pending'], ['active', CheckCircle, 'Active'], ['rejected', XCircle, 'Rejected'], ['all', ClipboardList, 'All']].map(([id, Icon, label]) => (
              <button key={id} className={`tab-btn ${filter === id ? 'active' : ''}`} onClick={() => setFilter(id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon size={14} /> {label} ({id === 'all' ? packages.length : counts[id] || 0})
              </button>
            ))}
          </div>

          <div className="card">
            {loading ? <div className="loader-wrap"><div className="loader"></div></div> :
              filtered.length === 0 ? (
                <div className="empty-state"><div className="empty-icon"><Package size={40} /></div><h3>No packages found</h3></div>
              ) : (
                <div className="table-wrap">
                  <table className="tp-table">
                    <thead>
                      <tr><th>Date</th><th>User</th><th>Mobile</th><th>Amount</th><th>Method</th><th>Wallet</th><th>UTR</th><th>Proof</th><th>Status</th><th>Note</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {filtered.map(pkg => (
                        <tr key={pkg._id}>
                          <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{new Date(pkg.createdAt).toLocaleDateString('en-IN')}</td>
                          <td>
                            <div style={{ fontWeight: 600 }}>{pkg.user?.name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{pkg.user?.email}</div>
                          </td>
                          <td style={{ fontSize: '0.85rem' }}>{pkg.user?.mobile}</td>
                          <td style={{ color: 'var(--primary)', fontWeight: 700 }}>{formatCurrency(pkg.amount)}</td>
                          <td><span className="badge badge-info">{pkg.paymentMethod}</span></td>
                          <td style={{ fontSize: '0.8rem' }}>
                            {pkg.paymentWallet ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Wallet size={12} /> {pkg.paymentWallet.label}</span>
                            ) : '—'}
                          </td>
                          <td style={{ fontSize: '0.82rem', maxWidth: 120, wordBreak: 'break-all' }}>{pkg.utrNumber || '—'}</td>
                          <td>
                            {pkg.paymentProof ? (
                              <button
                                type="button"
                                onClick={() => setViewingScreenshot(pkg.paymentProof)}
                                style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
                                title="View payment screenshot"
                              >
                                <img src={pkg.paymentProof} alt="Payment proof" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(var(--primary-rgb),0.25)' }} />
                              </button>
                            ) : (
                              <span style={{ color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem' }}><ImageIcon size={14} /> —</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge badge-${pkg.status === 'active' ? 'success' : pkg.status === 'pending' ? 'warning' : 'danger'}`}>
                              {pkg.status}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)', maxWidth: 120 }}>{pkg.adminNote || '—'}</td>
                          <td>
                            {pkg.status === 'pending' && (
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                  className="btn btn-success btn-sm"
                                  onClick={() => handleApprove(pkg._id)}
                                  disabled={actionLoading === pkg._id + '_approve'}
                                  style={{ fontSize: '0.75rem', padding: '6px 10px' }}
                                >
                                  {actionLoading === pkg._id + '_approve' ? '...' : (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle size={14} /> Approve</span>)}
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleReject(pkg._id)}
                                  disabled={actionLoading === pkg._id + '_reject'}
                                  style={{ fontSize: '0.75rem', padding: '6px 10px' }}
                                >
                                  {actionLoading === pkg._id + '_reject' ? '...' : (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><XCircle size={14} /> Reject</span>)}
                                </button>
                              </div>
                            )}
                            {pkg.status !== 'pending' && (
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                {pkg.activatedAt ? new Date(pkg.activatedAt).toLocaleDateString('en-IN') : 'Processed'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            }
          </div>
        </>
      ) : (
        <>
          {/* ===================== Manage Plans ===================== */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-primary btn-sm" onClick={openAddPlan} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Plus size={16} /> Add Plan
            </button>
          </div>

          <div className="card">
            {plansLoading ? <div className="loader-wrap"><div className="loader"></div></div> :
              plans.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon"><Layers size={40} /></div>
                  <h3>No plans yet</h3>
                  <p style={{ fontSize: '0.85rem', marginTop: 6 }}>Click "Add Plan" to create the first package users can purchase.</p>
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="tp-table">
                    <thead>
                      <tr><th>Name</th><th>Amount</th><th>Validity</th><th>Features</th><th>Visibility</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {plans.map(plan => (
                        <tr key={plan._id}>
                          <td style={{ fontWeight: 600 }}>
                            {plan.name}
                            {plan.description && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 400, marginTop: 2 }}>{plan.description}</div>}
                          </td>
                          <td style={{ color: 'var(--primary)', fontWeight: 700 }}>{formatCurrency(plan.amount)}</td>
                          <td style={{ fontSize: '0.85rem' }}>{plan.validityDays} days</td>
                          <td style={{ fontSize: '0.8rem', color: 'var(--text-light)', maxWidth: 220 }}>
                            {plan.features && plan.features.length > 0 ? plan.features.join(', ') : '—'}
                          </td>
                          <td>
                            <span className={`badge ${plan.isActive ? 'badge-success' : 'badge-warning'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              {plan.isActive ? <><Eye size={12} /> Visible</> : <><EyeOff size={12} /> Hidden</>}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button className="btn btn-outline btn-sm" onClick={() => openEditPlan(plan)} style={{ fontSize: '0.75rem', padding: '6px 10px' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Pencil size={14} /> Edit</span>
                              </button>
                              <button className="btn btn-danger btn-sm" onClick={() => handleDeletePlan(plan)} style={{ fontSize: '0.75rem', padding: '6px 10px' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Trash2 size={14} /> Delete</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            }
          </div>
        </>
      )}

      {/* Add/Edit Plan Modal */}
      {planModalOpen && (
        <div className="modal-overlay" onClick={closePlanModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingPlan ? 'Edit Plan' : 'Add Plan'}</h3>
              <button className="modal-close" onClick={closePlanModal}><X size={20} /></button>
            </div>
            <form onSubmit={handlePlanSubmit}>
              <div className="form-group">
                <label>Plan Name *</label>
                <input
                  type="text" name="name" className="form-control"
                  placeholder="e.g. Gold Plan"
                  value={planForm.name} onChange={handlePlanFormChange} required
                />
              </div>
              <div className="form-group">
                <label>Amount (₹) *</label>
                <input
                  type="number" name="amount" className="form-control"
                  placeholder="e.g. 10000"
                  value={planForm.amount} onChange={handlePlanFormChange} min={1} required
                />
              </div>
              <div className="form-group">
                <label>Validity (days)</label>
                <input
                  type="number" name="validityDays" className="form-control"
                  value={planForm.validityDays} onChange={handlePlanFormChange} min={1}
                />
              </div>
              <div className="form-group">
                <label>Short Description</label>
                <input
                  type="text" name="description" className="form-control"
                  placeholder="e.g. Best for steady long-term growth"
                  value={planForm.description} onChange={handlePlanFormChange}
                />
              </div>
              <div className="form-group">
                <label>Features (one per line)</label>
                <textarea
                  name="features" className="form-control" rows={4}
                  placeholder={'e.g.\n1% Daily ROI\nDirect + Level Income\nRoyalty eligible'}
                  value={planForm.features} onChange={handlePlanFormChange}
                />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="checkbox" id="planIsActive" name="isActive"
                  checked={planForm.isActive} onChange={handlePlanFormChange}
                  style={{ width: 18, height: 18 }}
                />
                <label htmlFor="planIsActive" style={{ margin: 0, cursor: 'pointer' }}>
                  Visible to users (uncheck to hide without deleting)
                </label>
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={planSubmitting} style={{ marginTop: 8 }}>
                {planSubmitting ? 'Saving...' : editingPlan ? 'Save Changes' : 'Create Plan'}
              </button>
            </form>
          </div>
        </div>
      )}
      {/* Payment Screenshot Lightbox */}
      {viewingScreenshot && (
        <div className="modal-overlay" onClick={() => setViewingScreenshot(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3 className="modal-title">Payment Screenshot</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  className="modal-close"
                  onClick={() => window.open(viewingScreenshot, '_blank', 'noopener,noreferrer')}
                  title="Open full image in new tab"
                >
                  <Maximize2 size={18} />
                </button>
                <button className="modal-close" onClick={() => setViewingScreenshot(null)}><X size={20} /></button>
              </div>
            </div>
            <img src={viewingScreenshot} alt="Payment proof full" style={{ width: '100%', borderRadius: 8 }} />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}